import GoNextBlockButton from "components/task/content/block/GoNextBlockButton";
import { Box, Sheet } from "@mui/joy";
import { useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";
import {
  getAiCommentKeyInBlock,
  getTaskIdFromTaskKey,
  isBlockUserFieldFilled,
  isLineHidden,
} from "logic/logics";
import { layoutAtom } from "recoil/layoutAtom";
import { ProgramContentType } from "data/BlockComponent";
import React, { memo, useEffect, useRef } from "react";
import { CommentArrowSvg } from "static/image/common/SvgAssets";
import useDetectIOS from "hooks/useDetectIOS";
import type { TaskContentV2ViewerMode } from "../TaskContentV2";
import LineV2 from "./LineV2";
import DoneButton from "components/task/change-status-button/DoneButton";
import CoachMenuBar from "components/task/content/block/CoachMenuBar";

interface PropsType {
  taskKey: string;
  data: ProgramContentType[];
  setData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  blockData: ProgramContentType;
  index: number;
  complete: (index: number) => void;
  isCurrentIndex: boolean;
  isLastIndex: boolean;
  viewerMode?: TaskContentV2ViewerMode;
  disableAutoDivider?: boolean;
  isBlockFilled?: boolean;
  doneWithEnter?: boolean;
  setDoneWithEnter?: React.Dispatch<React.SetStateAction<boolean>>;
  setMoveToIndex?: React.Dispatch<React.SetStateAction<number | undefined>>;
}

const BlockV2 = React.forwardRef<HTMLDivElement, PropsType>(
  (
    {
      taskKey,
      data,
      setData,
      blockData,
      index,
      complete,
      isCurrentIndex,
      isLastIndex,
      viewerMode = "auto",
      disableAutoDivider = false,
      isBlockFilled,
      doneWithEnter,
      setDoneWithEnter,
      setMoveToIndex,
    }: PropsType,
    ref
  ) => {
    const divRef = useRef<HTMLDivElement | null>(null);

    const user = useRecoilValue(userAtom);

    const isCoach =
      viewerMode === "auto"
        ? (!!user?.accessToken && user?.role !== "patient") || false
        : viewerMode === "coach";
    const isThoughtRecord = taskKey.includes("2-2-T");
    const isReadOnlyTask = taskKey.includes("0-3-W");
    const { isIOS } = useDetectIOS();

    const { taskWidth } = useRecoilValue(layoutAtom);

    const highlight = blockData.isHighlight;
    const isCurrentBlockFilled =
      isBlockFilled ?? isBlockUserFieldFilled(blockData, data, user);

    const isHidden =
      blockData.isHidden ||
      blockData.isAlwaysHidden ||
      (isCoach && !blockData.isShown && blockData.hideIfCoach) ||
      (!isCoach && (!blockData.isShown || blockData.hideIfPatient));

    const focusElement = (el: HTMLElement) => {
      el.focus();
      try {
        const input = el as HTMLInputElement | HTMLTextAreaElement;
        const len = input.value?.length || 0;
        input.setSelectionRange?.(len, len);
      } catch {}

      if (isIOS) {
        try {
          el.click();
        } catch {}
      }
    };

    useEffect(() => {
      if (isCurrentIndex && !isCoach && divRef.current) {
        const focusables = Array.from(
          divRef.current.querySelectorAll<HTMLElement>(
            'textarea, input[type="text"], input[type="number"], input[type="tel"], .ql-editor'
          )
        );
        const inputEl = focusables.find(
          (el) =>
            !el.closest("[data-feedback-cell]") &&
            !el.closest("[data-disable-autofocus]")
        );
        if (inputEl) {
          focusElement(inputEl);
        }
      }
    }, [isCurrentIndex, isCoach]);

    return (
      <div
        ref={(el) => {
          divRef.current = el;
          if (typeof ref === "function") {
            ref(el);
          } else if (ref) {
            (ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
          }
        }}
        key={`${taskKey}_${index}`}
        style={{
          width: "100%",
          margin: "0 auto",
          marginBottom: "8px",
          ...(taskWidth === "normal" && { maxWidth: "660px" }),
          ...(isHidden && { display: "none" }),
          ...(!blockData.isShown && { opacity: 0.5 }),
        }}
      >
        <div className={`${blockData.indentation && "flex items-start gap-[10px]"}`}>
          {blockData.indentation && <CommentArrowSvg />}

          <Sheet
            sx={{
              position: "relative",
              width: "100%",
              ...(!blockData.noBorder
                ? {
                    backgroundColor: "white",
                    borderRadius: "12px",
                    px: "20px",
                    py: "16px",
                  }
                : {
                    backgroundColor: "transparent",
                    py: "12px",
                    pl: "10px",
                    ...(index > 0 && { pt: "24px" }),
                  }),
            }}
          >
            {highlight && (
              <Box
                sx={{
                  backgroundColor: "primary.solid",
                  width: "5px",
                  height: "calc(100% - 40px)",
                  borderRadius: "10px",
                  position: "absolute",
                  top: "50%",
                  left: 0,
                  transform: "translate(0, -50%)",
                }}
              />
            )}

            {blockData.lines?.map(
              (eachLine, lineIndex) =>
                !isLineHidden(data, blockData, eachLine) && (
                  <LineV2
                    key={`${taskKey}_line_${lineIndex}`}
                    lineStr={JSON.stringify(eachLine)}
                    lineIndex={lineIndex}
                    taskKey={taskKey}
                    setData={setData}
                    blockIndex={index}
                    isLastLine={lineIndex === blockData.lines.length - 1}
                    readonly={false}
                    viewerMode={viewerMode}
                    hasDivider={
                      !disableAutoDivider &&
                      lineIndex === 0 &&
                      blockData.lines[lineIndex].length === 1 &&
                      blockData.lines.length > 1 &&
                      [
                        "typography",
                        "thoughtmap",
                        "preset",
                        "reference",
                        "conceptItem",
                        "isolation",
                      ].includes(blockData.lines[lineIndex + 1][0].type)
                    }
                  />
                )
            )}
            {!isCoach &&
              (isLastIndex || blockData.isEnd) &&
              !taskKey.includes("Tool") && (
                <Box sx={{ mt: "12px" }}>
                  <DoneButton
                    taskKey={taskKey}
                    data={data}
                    doneWithEnter={doneWithEnter}
                    setDoneWithEnter={setDoneWithEnter}
                    setMoveToIndex={setMoveToIndex}
                    isDone={undefined}
                    isOpen={undefined}
                    translationVersion={undefined}
                  />
                </Box>
              )}
          </Sheet>
        </div>

        {!isLastIndex && !blockData.isEnd && (
          <>
            {!isCoach && !blockData.noArrow && (
              <GoNextBlockButton
                highlight={
                  getTaskIdFromTaskKey(taskKey) === "2-3-A" && index === 0
                }
                disabled={
                  !(isCurrentIndex && isCurrentBlockFilled)
                }
                onClick={() => {
                  complete(index);
                }}
              />
            )}
            {isCoach &&
              !isReadOnlyTask &&
              (!blockData.noBorder || blockData.removable) &&
              !blockData.hideIfPatient && (
                <CoachMenuBar
                  taskKey={taskKey}
                  setData={setData}
                  index={index}
                  isThoughtRecord={isThoughtRecord}
                  hasIndentation={blockData.indentation}
                  isRemovable={blockData.removable}
                  aiCommentKey={getAiCommentKeyInBlock(blockData)}
                />
              )}
          </>
        )}
      </div>
    );
  }
);

export default memo(BlockV2);
